import type { LatLon } from '../../lib/types'

/**
 * Rechnungen für den Kartenausschnitt.
 *
 * MapLibre kann Ausschnitte selbst berechnen (fitBounds), aber nur an einer
 * lebenden Karte: es braucht die Grösse der Zeichenfläche und einen
 * fertigen Renderer. Für die kleine Etappenkarte und später für den
 * Bildexport muss der Ausschnitt aber schon feststehen, bevor es eine Karte
 * gibt - sonst springt sie nach dem Aufbau noch einmal. Deshalb liegt die
 * Mathematik hier: reine Funktionen, ohne MapLibre und ohne DOM, und damit
 * ohne Browser testbar.
 *
 * Gerechnet wird in der Web-Mercator-Einheitsfläche: die ganze Welt ist ein
 * Quadrat von 0 bis 1, und Zoomstufe z bedeutet, dass dieses Quadrat
 * 256 * 2^z Bildpunkte breit ist.
 */

/** Kantenlänge einer Kachel in Bildpunkten. Web-Mercator-Konvention. */
const TILE_SIZE = 256

/** Äusserste Breite, die Mercator noch abbilden kann. */
const MAX_LAT = 85.051129

/** Ausschnitt in der Schreibweise von MapLibre: Mittelpunkt als [Länge, Breite]. */
export interface Viewport {
  center: [number, number]
  zoom: number
}

export interface ViewportOptions {
  /** Breite der Zeichenfläche in Bildpunkten. */
  width?: number
  /** Höhe der Zeichenfläche in Bildpunkten. */
  height?: number
  /** Rand, der frei bleiben soll, in Bildpunkten. */
  padding?: number
  minZoom?: number
  maxZoom?: number
}

/** Südwest- und Nordost-Ecke als [Länge, Breite], wie MapLibre sie erwartet. */
export type Bounds = [[number, number], [number, number]]

function clampLat(lat: number): number {
  return Math.max(-MAX_LAT, Math.min(MAX_LAT, lat))
}

/** Länge auf 0..1. */
export function mercatorX(lon: number): number {
  return (lon + 180) / 360
}

/** Breite auf 0..1, von Norden nach Süden wachsend. */
export function mercatorY(lat: number): number {
  const s = Math.sin((clampLat(lat) * Math.PI) / 180)
  return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)
}

/** Umkehrung von mercatorX. */
export function lonOf(x: number): number {
  return x * 360 - 180
}

/** Umkehrung von mercatorY. */
export function latOf(y: number): number {
  const n = Math.PI * (1 - 2 * y)
  return (Math.atan(Math.sinh(n)) * 180) / Math.PI
}

/**
 * Legt eine Punktmenge so in eine Zeichenfläche, dass alle mit Rand sichtbar
 * sind.
 *
 * Genau das braucht die Etappenkarte: alles, was zu dieser einen Etappe
 * gehört, und sonst nichts. Hier standen einmal genau zwei Punkte, Start und
 * Ziel - und damit war der Ausschnitt einer Runde ein Punkt, denn sie endet,
 * wo sie beginnt. Was hineingehört, entscheidet der Aufrufer (stageExtent in
 * features/map/routeGeoJson.ts); diese Rechnung rahmt ein, was sie bekommt.
 *
 * Drei Grenzfälle muss sie aushalten, und alle drei kommen in den echten Daten
 * vor:
 *
 *  - Alle Punkte liegen aufeinander (ein Standtag ohne Fahrt). Die Spannweite
 *    ist null, die Zoomstufe wäre unendlich. Dann gilt maxZoom.
 *  - Die Punkte liegen sehr weit auseinander (Bern bis zum Schwarzen Meer).
 *    Dann darf die Zoomstufe nicht unter minZoom fallen, sonst verlangt
 *    MapLibre eine Stufe, die es nicht darstellen kann.
 *  - Es gibt gar keinen Punkt. Dann steht der Mittelpunkt bei null Grad; ein
 *    erfundener Ort wäre schlechter als der Nullpunkt, den man als solchen
 *    erkennt.
 */
export function viewportForPoints(points: LatLon[], options: ViewportOptions = {}): Viewport {
  const width = Math.max(1, options.width ?? 360)
  const height = Math.max(1, options.height ?? 240)
  const padding = Math.max(0, options.padding ?? 32)
  const minZoom = options.minZoom ?? 0
  const maxZoom = options.maxZoom ?? 12

  let westX = Infinity
  let ostX = -Infinity
  let nordY = Infinity
  let suedY = -Infinity

  for (const [lat, lon] of points) {
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue
    const x = mercatorX(lon)
    const y = mercatorY(lat)
    if (x < westX) westX = x
    if (x > ostX) ostX = x
    if (y < nordY) nordY = y
    if (y > suedY) suedY = y
  }

  if (westX === Infinity) return { center: [0, 0], zoom: minZoom }

  const center: [number, number] = [lonOf((westX + ostX) / 2), latOf((nordY + suedY) / 2)]

  const spanX = ostX - westX
  const spanY = suedY - nordY

  // Es bleibt auf jeder Seite ein Rand frei, also steht weniger Fläche zur
  // Verfügung als die Zeichenfläche breit ist.
  const usableWidth = Math.max(1, width - padding * 2)
  const usableHeight = Math.max(1, height - padding * 2)

  // Wie viele Bildpunkte die ganze Welt haben darf, damit die Spannweite noch
  // in die nutzbare Fläche passt. Eine Spannweite von null heisst: beliebig
  // viele, deshalb Infinity - der Vergleich weiter unten fängt das ab.
  const worldByWidth = spanX > 0 ? usableWidth / spanX : Infinity
  const worldByHeight = spanY > 0 ? usableHeight / spanY : Infinity
  const world = Math.min(worldByWidth, worldByHeight)

  const zoom = world === Infinity ? maxZoom : Math.log2(world / TILE_SIZE)

  return { center, zoom: Math.max(minZoom, Math.min(maxZoom, zoom)) }
}

/**
 * Umschliessendes Rechteck einer Punktmenge.
 *
 * Für die Übersichtskarte reicht das: den Rest - Zoomstufe, Rand,
 * Seitenverhältnis - erledigt fitBounds an der lebenden Karte, die ihre
 * eigene Grösse kennt.
 *
 * @returns null, wenn es nichts einzurahmen gibt.
 */
export function boundsOf(points: LatLon[]): Bounds | null {
  let west = Infinity
  let east = -Infinity
  let south = Infinity
  let north = -Infinity

  for (const [lat, lon] of points) {
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue
    if (lon < west) west = lon
    if (lon > east) east = lon
    if (lat < south) south = lat
    if (lat > north) north = lat
  }

  if (west === Infinity) return null
  return [
    [west, south],
    [east, north],
  ]
}
